import React, { useState } from 'react';
import { Container, Header, Box, SpaceBetween, Spinner, StatusIndicator } from '@amzn/awsui-components-react-v3';
import { ErrorBoundary } from 'react-error-boundary';
import { usePanelData } from '../../hooks/usePanelData';
import { QueryScope, FunnelStage } from '../../interfaces/panel-data';
import { Badge } from './Badge';
import { Legend } from './Legend';
import { StageBar } from './StageBar';
import { DrillDownRow } from './DrillDownRow';
import { TopReasonsChart } from './TopReasonsChart';
import './panel-tokens.scss';
import './FunnelPanel.scss';

const STAGE_COLORS: Record<string, string> = {
  SDO: 'var(--ovp-sdo)',
  SCI: 'var(--ovp-sci)',
  DTP: 'var(--ovp-dtp)',
};

interface FunnelPanelProps {
  scope: QueryScope;
  timeWindow?: string;
}

/**
 * Renders the backend `asOf` snapshot id (`yyyy-MM-dd-HH`, UTC) for display. Falls back to the raw
 * value if the format is ever something else, so an unexpected id shows rather than breaking.
 */
const formatAsOf = (asOf: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})-(\d{2})$/.exec(asOf);
  return match ? `${match[1]}-${match[2]}-${match[3]} ${match[4]}:00 UTC` : asOf;
};

const FunnelPanelBody: React.FC<FunnelPanelProps> = ({ scope, timeWindow = '30D' }) => {
  const [expandedStage, setExpandedStage] = useState<string | null>(null);

  // COHORT aggregates across every website, so it has nothing to wait for. WEBSITE does: without a
  // websiteId the backend rejects the request rather than guessing, so there is no point sending it.
  const { data, isLoading, error } = usePanelData('FUNNEL', scope, {
    timeWindow,
    enabled: scope.type === 'COHORT' || !!scope.websiteId || !!scope.program,
  });

  const renderDrillContent = (stage: FunnelStage) => (
    <>
      <div className="ovp-drill__detail-title">{stage.name} — loss by sub-stage</div>
      {stage.subStages?.map((sub) => (
        <div key={sub.name} className="ovp-drill__substage">
          <div className="ovp-drill__substage-label">
            {sub.name}
            <small>{sub.label}</small>
          </div>
          <div className="ovp-drill__substage-bar">
            <StageBar
              retained={sub.retained}
              lost={sub.lost}
              retainedColor={STAGE_COLORS[stage.name] || 'var(--ovp-blue)'}
              small
            />
          </div>
          <div className="ovp-drill__substage-nums">
            {sub.itemsIn.toLocaleString()} in ·{' '}
            {sub.lost > 0 ? (
              <span className="ovp-up">{sub.lost.toLocaleString()} lost ({sub.lossPercent}%)</span>
            ) : (
              <span className="ovp-flat">0 lost</span>
            )}
          </div>
        </div>
      ))}
      {stage.topReasons && stage.topReasons.length > 0 && (
        <TopReasonsChart title={`Top drop reasons — ${stage.name}`} reasons={stage.topReasons} />
      )}
    </>
  );

  const headerContent = (
    <SpaceBetween direction="horizontal" size="xs">
      <span>Funnel loss</span>
      {data && (
        <Badge variant="error">
          ⊘ {data.summary.totalLossPercent}% total loss · {data.summary.totalLostItems.toLocaleString()} items
        </Badge>
      )}
      {data?.asOf && <span className="ovp-funnel__as-of">Snapshot {formatAsOf(data.asOf)}</span>}
    </SpaceBetween>
  );

  return (
    <Box margin={{ bottom: 'l' }}>
      <Container header={<Header variant="h2" description="Item drop-off across pipeline stages SDO → SCI → DTP. Where was the loss, and why?">{headerContent}</Header>}>
        {isLoading ? (
          <SpaceBetween direction="horizontal" size="xs">
            <Spinner />
            <span>Loading funnel data...</span>
          </SpaceBetween>
        ) : error ? (
          <StatusIndicator type="error">Failed to load funnel data.</StatusIndicator>
        ) : data ? (
          <div className="ovp-funnel">
            <Legend
              items={[
                { label: 'Retained (passed to next stage)', type: 'keep' },
                { label: 'Lost at this stage', type: 'lost' },
              ]}
              note="bars scaled to items entering each stage"
            />
            <div className="ovp-funnel__hint">
              Click a stage to see per-sub-stage loss and the item-level drop reasons (with counts) driving it.
            </div>

            {data.stages.map((stage) => (
              <DrillDownRow
                key={stage.name}
                stageName={stage.name}
                stageColor={STAGE_COLORS[stage.name] || 'var(--ovp-blue)'}
                summaryText={`In ${stage.itemsIn.toLocaleString()} · retained ${stage.retained.toLocaleString()} · lost ${stage.lost.toLocaleString()} (${stage.lossPercent}%)`}
                bar={
                  <StageBar
                    retained={stage.retained}
                    lost={stage.lost}
                    retainedColor={STAGE_COLORS[stage.name] || 'var(--ovp-blue)'}
                    retainedLabel={`Retained ${stage.retained.toLocaleString()}`}
                    lostLabel={stage.lost > 0 ? `Lost ${stage.lost.toLocaleString()}` : undefined}
                  />
                }
                expanded={expandedStage === stage.name}
                onToggle={() => setExpandedStage((prev) => (prev === stage.name ? null : stage.name))}
              >
                {renderDrillContent(stage)}
              </DrillDownRow>
            ))}

            <div className="ovp-funnel__summary">
              <Badge variant="error">
                ⊘ {data.summary.totalLossPercent}% total loss · {data.summary.totalLostItems.toLocaleString()} items
              </Badge>{' '}
              Largest loss at <b>{data.summary.largestLossCapability} → {data.summary.largestLossReason}</b>
            </div>
          </div>
        ) : null}
      </Container>
    </Box>
  );
};

/** Declared outside the component so the boundary is not handed a new component type each render. */
const FunnelPanelError: React.FC = () => (
  <Box margin={{ bottom: 'l' }}>
    <Container header={<Header variant="h2">Funnel loss</Header>}>
      <StatusIndicator type="error">Failed to load funnel data.</StatusIndicator>
    </Container>
  </Box>
);

/**
 * The panel renders backend numbers directly (`sub.itemsIn.toLocaleString()`,
 * `data.summary.totalLossPercent`), so a payload missing a field throws *during render*. react-query
 * only catches throws inside its `queryFn`, and `AppInitWrapper.componentDidCatch` deliberately
 * rethrows to fail the whole app — so without a boundary here, one malformed funnel payload
 * white-screens the entire dashboard. Contain it to this panel instead: the rest of the page, and
 * the other panels landing in this folder, stay usable.
 *
 * `resetKeys` holds the scope primitives rather than the `scope` object, which callers pass as a
 * fresh literal — keying on the object would reset the boundary on every parent render, throw again,
 * and loop. Keyed this way, a bad payload for one website clears when the user picks another instead
 * of leaving the panel dead until a full navigation.
 */
export const FunnelPanel: React.FC<FunnelPanelProps> = (props) => (
  <ErrorBoundary
    resetKeys={[props.scope.type, props.scope.websiteId, props.scope.program, props.timeWindow]}
    FallbackComponent={FunnelPanelError}
  >
    <FunnelPanelBody {...props} />
  </ErrorBoundary>
);
